import { useState } from "react";
import { BrandName } from "@/shared/branding";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Mountain, Mail, Lock, ArrowRight, Loader2 } from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { login as loginApi } from "@/services/auth";
import { apiConfig, ApiError } from "@/services/api";
import { MOCK_USERS } from "@/services/mock/users";
import { ROLE_LABEL } from "@/services/types/auth";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const loginSuccess = useAuthStore((s) => s.loginSuccess);
  // PWRESET-1 顺手清掉：真后端模式此前预填 admin@youtu.travel / Admin@2026——
  // 公司机登录页也会带着这组个人机默认账号密码，不该出现在任何现网登录框里。
  const [email, setEmail] = useState(apiConfig.useMock ? MOCK_USERS[0]?.email ?? "" : "");
  const [password, setPassword] = useState(apiConfig.useMock ? "demo" : "");
  const [mfaCode, setMfaCode] = useState("");
  const [needsMfa, setNeedsMfa] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? "/";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await loginApi({
        email,
        password,
        mfa_code: needsMfa ? mfaCode : undefined,
      });
      if (result.kind === "mfa_enrollment_required") {
        // MFAFIX-1 · 死锁分支：角色强制 MFA 但账号还没绑定过——跳绑定页，
        // enrollmentToken 只通过 router state 传（不落 authStore，那里的令牌
        // 语义是"已登录"，混进去会让 AuthGuard/api.ts 误判已登录）。
        navigate("/mfa-enroll", { state: { enrollmentToken: result.enrollmentToken }, replace: true });
        return;
      }
      loginSuccess(result.user, result.tokens);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        // backend: detail "需要 MFA 验证码"
        if (err.status === 401 && /MFA/i.test(err.message)) {
          setNeedsMfa(true);
          setError(err.message);
        } else if (err.status === 423) {
          setError(err.message || "账户已锁定");
        } else {
          setError(err.message || "登录失败");
        }
      } else {
        setError("网络错误 · 请稍后重试");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper p-6">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600">
            <Mountain className="h-5 w-5 text-white" strokeWidth={2} />
          </div>
          <div className="flex flex-col">
            <span className="text-lg font-bold text-slate-900"><BrandName /></span>
            <span className="text-[12px] text-slate-500">
              运营后台 · v1 {apiConfig.useMock ? "(mock 模式)" : ""}
            </span>
          </div>
        </div>

        <h1 className="mb-1 text-xl font-bold text-slate-900">登录</h1>
        <p className="mb-6 text-[13px] text-slate-500">
          {apiConfig.useMock
            ? "使用任一 mock 邮箱登录（任意密码）"
            : "使用邮箱密码登录 · MFA 仅对敏感角色启用"}
        </p>

        <form onSubmit={submit} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[12px] font-medium text-slate-700">邮箱</span>
            <div className="flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100">
              <Mail className="h-4 w-4 text-slate-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                className="flex-1 bg-transparent text-[13px] outline-none"
                required
              />
            </div>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[12px] font-medium text-slate-700">密码</span>
            <div className="flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100">
              <Lock className="h-4 w-4 text-slate-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="flex-1 bg-transparent text-[13px] outline-none"
                required
              />
            </div>
          </label>

          {needsMfa && (
            <label className="flex flex-col gap-1">
              <span className="text-[12px] font-medium text-slate-700">MFA 验证码（6 位）</span>
              <div className="flex h-10 items-center gap-2 rounded-lg border border-amber-400 bg-amber-50 px-3 focus-within:ring-2 focus-within:ring-amber-200">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  placeholder="000000"
                  className="flex-1 bg-transparent text-[13px] outline-none tracking-widest"
                  required
                />
              </div>
            </label>
          )}

          {error && <div className="text-[12px] font-medium text-rose-600">{error}</div>}

          {!apiConfig.useMock && (
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-[12px] font-medium text-blue-600 hover:underline">
                忘记密码？
              </Link>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="mt-1 flex h-10 items-center justify-center gap-1.5 rounded-lg bg-blue-600 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                登录 <ArrowRight className="h-3.5 w-3.5" />
              </>
            )}
          </button>
        </form>

        <div className="my-5 flex items-center gap-3 text-[11px] text-slate-400">
          <div className="h-px flex-1 bg-slate-200" />
          <span>或</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        <Link
          to="/sso"
          className="flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white text-[13px] font-medium text-slate-900 hover:bg-slate-50"
        >
          <GoogleIcon /> Google Workspace SSO
        </Link>

        {apiConfig.useMock && (
          <details className="mt-5 rounded-lg bg-slate-50 p-3 text-[12px]">
            <summary className="cursor-pointer font-medium text-slate-700">
              Dev · 快速切角色（mock）
            </summary>
            <div className="mt-2 grid grid-cols-2 gap-1.5">
              {MOCK_USERS.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => {
                    loginSuccess(u, {
                      accessToken: `mock-access-${u.id}`,
                      refreshToken: `mock-refresh-${u.id}`,
                      expiresIn: 3600,
                    });
                    navigate("/", { replace: true });
                  }}
                  className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-left text-[11px] hover:border-blue-300 hover:bg-blue-50"
                >
                  <div className="font-semibold text-slate-900">{u.name}</div>
                  <div className="text-slate-500">{ROLE_LABEL[u.role]}</div>
                </button>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.09-1.92 3.28-4.74 3.28-8.07z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.12A6.7 6.7 0 0 1 5.5 12c0-.74.13-1.46.34-2.12V7.04H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.96l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.07.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
      />
    </svg>
  );
}
